import { MessageCircle, Mail, Zap } from "lucide-react";
import { useLang } from "../i18n";
import { Reveal, LogoFull } from "./common";
import {
    PHONE_DISPLAY,
    PHONE_TEL,
    WHATSAPP_2_DISPLAY,
    WHATSAPP_2_RAW,
    EMAIL,
    wa,
    DEFAULT_MSG_PT,
    DEFAULT_MSG_EN,
    scrollToId,
} from "../lib/site";

export default function Footer() {
    const { lang, t } = useLang();
    const msg = lang === "pt" ? DEFAULT_MSG_PT : DEFAULT_MSG_EN;
    const year = new Date().getFullYear();

    return (
        <footer id="contato" className="relative bg-deep border-t border-white/10 overflow-hidden">
            <div className="max-w-7xl mx-auto px-5 md:px-10 pt-24 md:pt-32 pb-10">
                <Reveal className="text-center max-w-3xl mx-auto">
                    <h2 className="font-poster uppercase tracking-[0.01em] font-normal text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                        {t.footer.ctaTitle.split(" ").slice(0, -2).join(" ")}{" "}
                        <span className="text-volt">
                            {t.footer.ctaTitle.split(" ").slice(-2).join(" ")}
                        </span>
                    </h2>
                    <p className="mt-5 text-slate-400">{t.footer.ctaSub}</p>

                    <a
                        data-testid="footer-phone-link"
                        href={`tel:+55${WHATSAPP_2_RAW.slice(2)}`}
                        className="mt-10 inline-block font-poster text-4xl sm:text-5xl md:text-6xl text-volt hover:drop-shadow-[0_0_30px_rgba(255,230,0,0.5)] transition-all tracking-wide"
                    >
                        {WHATSAPP_2_DISPLAY}
                    </a>

                    <div className="mt-9">
                        <a
                            data-testid="footer-whatsapp-cta"
                            href={wa(msg)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-3 bg-volt text-obsidian font-bold px-8 py-4 rounded-full hover:shadow-[0_0_40px_rgba(255,230,0,0.45)] hover:-translate-y-0.5 transition-all"
                        >
                            <MessageCircle className="w-5 h-5" />
                            {t.footer.ctaBtn}
                        </a>
                    </div>
                </Reveal>

                <div
                    className="mt-12 flex flex-wrap justify-center gap-3"
                    data-testid="footer-contacts"
                >
                    <a
                        data-testid="footer-contact-wa1"
                        href={wa(msg)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-slate-300 border border-white/15 rounded-full px-4 py-2 hover:border-volt hover:text-volt transition-all"
                    >
                        <MessageCircle className="w-3.5 h-3.5 text-[#25D366]" />
                        WhatsApp · {PHONE_DISPLAY}
                    </a>
                    <a
                        data-testid="footer-contact-wa2"
                        href={`https://wa.me/${WHATSAPP_2_RAW}?text=${encodeURIComponent(msg)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-slate-300 border border-white/15 rounded-full px-4 py-2 hover:border-volt hover:text-volt transition-all"
                    >
                        <MessageCircle className="w-3.5 h-3.5 text-[#25D366]" />
                        WhatsApp · {WHATSAPP_2_DISPLAY}
                    </a>
                    <a
                        data-testid="footer-contact-email"
                        href={`mailto:${EMAIL}`}
                        className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.08em] text-slate-300 border border-white/15 rounded-full px-4 py-2 hover:border-volt hover:text-volt transition-all"
                    >
                        <Mail className="w-3.5 h-3.5 text-volt" />
                        {EMAIL}
                    </a>
                </div>

                <div className="mt-24 pt-12 border-t border-white/10 grid gap-10 md:grid-cols-4">
                    <div>
                        <LogoFull width={150} />
                        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 leading-loose">
                            Engenharia · Comércio & Serviços
                        </p>
                    </div>

                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-volt mb-4">
                            {t.footer.navLabel}
                        </p>
                        <ul className="space-y-2.5 text-sm text-slate-400">
                            {[
                                ["inicio", t.nav.home],
                                ["equipe", t.nav.team],
                                ["servicos", t.nav.services],
                                ["ti", t.nav.ti],
                                ["spda", t.nav.spda],
                                ["obras", t.nav.works],
                                ["cobertura", t.nav.coverage],
                                ["orcamento", t.nav.quote],
                            ].map(([id, label]) => (
                                <li key={id}>
                                    <button
                                        onClick={() => scrollToId(id)}
                                        className="hover:text-volt transition-colors"
                                    >
                                        {label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-volt mb-4">
                            {t.footer.servicesLabel}
                        </p>
                        <ul className="space-y-2.5 text-sm text-slate-400">
                            {t.footer.serviceItems.map((s) => (
                                <li key={s} className="flex items-center gap-2">
                                    <Zap className="w-3 h-3 text-volt/60 shrink-0" />
                                    {s}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-volt mb-4">
                            {t.footer.regionsLabel}
                        </p>
                        <ul className="space-y-2.5 text-sm text-slate-400">
                            {t.footer.regions.map((r) => (
                                <li key={r}>{r}</li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="mt-14 pt-7 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-3 text-[11px] font-mono text-slate-600">
                    <span>
                        © {year} ANG — Engenharia, Comércio & Serviços. {t.footer.rights}
                    </span>
                    <span>{PHONE_DISPLAY} · São Paulo — SP</span>
                </div>
            </div>
        </footer>
    );
}
